import { Box, Typography, IconButton, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Button, Avatar } from "@mui/material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAttributesService } from '../../../../../shared/services/userAtributes/userAtributes';
import React, { useState } from 'react';
import VehicleForm from './Form';

interface VehicleListProps {
  userId: string;
}

export const VehicleList = ({ userId }: VehicleListProps) => {
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [editInitial, setEditInitial] = useState<any>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['user', userId, 'veiculo'],
    queryFn: () => userAttributesService.get(userId, 'veiculo?pattern=true'),
    enabled: !!userId,
  });

  const mutation = useMutation({
    mutationFn: async (vehicleEditado: any) => {
      if (!data || !data.data) {
        throw new Error('Dados do atributo não carregados');
      }
      if (editIdx === null) throw new Error('Invalid index for editing');
      
      const vehicle = vehicles[editIdx];
      const itemId = vehicle.vehicleId;
      const attributeKey = vehicle.key; 
      
      // Se tem arquivo novo para upload
      if (vehicleEditado.foto && vehicleEditado.foto.length > 0 && vehicleEditado.foto[0] instanceof File) {
        const formData = new FormData();
        formData.append('userId', userId);
        formData.append('key', attributeKey);

        // Adiciona os campos individualmente, igual ao backend espera
        formData.append('marca', vehicleEditado.marca);
        formData.append('modelo', vehicleEditado.modelo);
        formData.append('tipo', vehicleEditado.tipo);
        formData.append('placa', vehicleEditado.placa);
        formData.append('ano', vehicleEditado.ano);
        formData.append('cor', vehicleEditado.cor || '');
        formData.append('observacoes', vehicleEditado.observacoes || '');

        formData.append('file', vehicleEditado.foto[0]);
        
        return userAttributesService.updateValueWithFile(itemId, formData);
      } else {
        const vehicleData = { ...vehicleEditado };
        delete vehicleData.foto;
        
        return userAttributesService.updateValue(itemId, {
          userId,
          key: attributeKey,
          ...vehicleData 
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId, 'veiculo?pattern=true'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      setOpenEdit(false);
      setEditIdx(null);
      setEditInitial(null);
    },
    onError: (a) => {
     console.log('Erro ao atualizar veículo:', a);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return userAttributesService.removeValue(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', userId, 'veiculo?pattern=true'] });
      queryClient.invalidateQueries({ queryKey: ['user', userId] });
      setOpenConfirm(false);
      setDeleteIdx(null);
    },
  });

  // Função para deletar
  const handleDelete = () => {
    if (deleteIdx === null || vehicles.length === 0) return;
    
    const vehicleToDelete = vehicles[deleteIdx];
    if (!vehicleToDelete || !vehicleToDelete.vehicleId) return;
    
    deleteMutation.mutate(vehicleToDelete.vehicleId);
  };

  // Função para editar
  const handleEdit = (idx: number, vehicle: any) => {
    setEditIdx(idx);
    setEditInitial(vehicle);
    setOpenEdit(true);
  };

  const handleEditSave = (vehicleEditado: any) => {
    if (editIdx === null || !data || !data.data) return;
    
    mutation.mutate(vehicleEditado);
  };

  // Processar a estrutura correta dos dados
  let vehicles: any[] = [];
  if (data && data.data && Array.isArray(data.data)) {
    data.data.forEach((attribute: any) => {
      if (attribute.value && Array.isArray(attribute.value)) {
        attribute.value.forEach((item: any) => {
          // NÃO usar JSON.parse, pois item já é objeto pronto
          vehicles.push({
            ...item,
            vehicleId: item.id,
            attributeId: attribute.id,
            key: attribute.key,
            imageUrl: item.imageUrl
          });
        });
      }
    });
  }

  // Agora o return pode ser condicional, mas SEM hooks dentro!
  if (isLoading) return <Typography>Carregando...</Typography>;
  if (vehicles.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', color: '#9CA3AF', py: 3 }}>
        <DirectionsCarIcon sx={{ fontSize: 40, color: '#E0E7EF', mb: 1 }} />
        <Typography color="text.secondary">Nenhum veículo cadastrado.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {vehicles.length === 0 ? (
        <Box sx={{ textAlign: 'center', color: '#9CA3AF', py: 3 }}>
          <DirectionsCarIcon sx={{ fontSize: 40, color: '#E0E7EF', mb: 1 }} />
          <Typography color="text.secondary">Nenhum veículo cadastrado.</Typography>
        </Box>
      ) : (
        <Stack gap={2}>
          {vehicles.map((vehicle, idx) => (
            <Box
              key={vehicle.vehicleId || idx}
              sx={{
                background: '#fff',
                borderRadius: 2,
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                boxShadow: '0 1px 2px #e5e7eb',
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar 
                    src={vehicle.imageUrl} 
                    sx={{ width: 40, height: 40, bgcolor: '#E0E7EF' }}
                  >
                    <DirectionsCarIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
                  </Avatar>
                  <Box>
                    <Typography fontWeight={700} fontSize={17} color="#23272E">
                      {vehicle.marca} {vehicle.modelo || 'Veículo sem nome'}
                    </Typography>
                    <Typography fontSize={15} color="#6B7280">
                      {vehicle.placa} • {vehicle.ano} • {vehicle.cor || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <IconButton size="small" onClick={() => handleEdit(idx, vehicle)}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => { setDeleteIdx(idx); setOpenConfirm(true); }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
      
      {/* Modal de confirmação de deleção */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Remover Veículo</DialogTitle>
        <DialogContent>
          Tem certeza que deseja remover este veículo?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleDelete} color="error" disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Removendo...' : 'Remover'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Modal de edição de veículo */}
      <VehicleForm
        openVehicleModal={openEdit}
        setOpenVehicleModal={setOpenEdit}
        userId={userId}
        initialData={editInitial}
        onSave={handleEditSave}
        editMode
      />
    </Box>
  );
}